import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { PlatformToastPreferenceContext, PlatformToastPreferenceContextValue } from './preferenceContext';

interface PlatformToastPreferenceProviderProps extends PlatformToastPreferenceContextValue {
    children: ReactNode;
}

/**
 * Supplies persisted, authenticated toast presentation preferences without
 * coupling the shared card to authenticated-only data fetching.
 */
export const PlatformToastPreferenceProvider: FC<PlatformToastPreferenceProviderProps> = ({
    bodyExpandedByDefault,
    children,
    onBodyExpandedPreferenceChange,
}) => {
    const [bodyExpanded, setBodyExpanded] = useState(bodyExpandedByDefault);

    useEffect(() => {
        setBodyExpanded(bodyExpandedByDefault);
    }, [bodyExpandedByDefault]);

    const handleBodyExpandedPreferenceChange = useCallback(
        (expanded: boolean) => {
            setBodyExpanded(expanded);
            onBodyExpandedPreferenceChange?.(expanded);
        },
        [onBodyExpandedPreferenceChange],
    );

    const value = useMemo(
        () => ({
            bodyExpandedByDefault: bodyExpanded,
            onBodyExpandedPreferenceChange: handleBodyExpandedPreferenceChange,
        }),
        [bodyExpanded, handleBodyExpandedPreferenceChange],
    );

    return <PlatformToastPreferenceContext.Provider value={value}>{children}</PlatformToastPreferenceContext.Provider>;
};
